/**
 * SystemParametersPage — `/settings/system-parameters`.
 *
 * Tunes platform-wide operational parameters via the real
 * `GET /v1/platform-settings` + `PUT /v1/platform-settings/:key` endpoints
 * on service-audit (B slice 6).
 */
import { type JSX, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, RotateCcw, Save } from 'lucide-react';

import { listPlatformSettings, upsertPlatformSetting } from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

interface Group {
  readonly name: string;
  readonly keys: readonly string[];
}

const GROUPS: readonly Group[] = [
  {
    name: 'Customer Settings',
    keys: ['max_addresses_per_customer', 'customer_inactivity_period_days', 'max_saved_payment_methods'],
  },
  {
    name: 'Session Settings',
    keys: ['session_timeout_minutes', 'otp_expiry_seconds', 'max_concurrent_sessions', 'refresh_token_ttl_days'],
  },
  {
    name: 'Security Settings',
    keys: ['max_login_attempts', 'lockout_duration_minutes', 'password_min_length', 'password_expiry_days'],
  },
  {
    name: 'Financial Settings',
    keys: ['settlement_frequency', 'reconciliation_tolerance_minor', 'float_top_up_threshold_pct'],
  },
];

export function SystemParametersPage(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState<readonly string[]>(GROUPS.map((g) => g.name));
  // local overrides: key → staged value (before save)
  const [staged, setStaged] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const settings = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => listPlatformSettings(),
    retry: false,
    staleTime: 30_000,
  });

  const settingMap: Record<string, unknown> = {};
  for (const s of settings.data?.items ?? []) {
    settingMap[s.key] = s.value;
  }

  function getDisplayValue(key: string): string {
    if (key in staged) return staged[key] ?? '';
    const v = settingMap[key];
    if (v === undefined || v === null) return '';
    return String(v).replace(/^"|"$/g, '');
  }

  function toggle(name: string): void {
    setOpen((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }

  function stageValue(key: string, value: string): void {
    setStaged((prev) => ({ ...prev, [key]: value }));
  }

  async function save(key: string): Promise<void> {
    const value = staged[key] ?? getDisplayValue(key);
    setSaving((prev) => ({ ...prev, [key]: true }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
    try {
      await upsertPlatformSetting(key, value);
      setStaged((prev) => {
        const next = { ...prev };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- key comes from the same controlled set just persisted
        delete next[key];
        return next;
      });
      await qc.invalidateQueries({ queryKey: ['platform-settings'] });
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [key]: err instanceof Error ? err.message : 'Save failed',
      }));
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  }

  function resetStaged(key: string): void {
    setStaged((prev) => {
      const next = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- key comes from the controlled settings catalogue
      delete next[key];
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- key comes from the controlled settings catalogue
      delete next[key];
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="System parameters"
        description="Tune platform-wide operational parameters by category."
      />

      {settings.isPending ? (
        <p className="text-sm text-muted">Loading parameters…</p>
      ) : settings.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Could not load platform settings: {(settings.error as Error).message}
        </div>
      ) : null}

      <div className="space-y-4">
        {GROUPS.map((group) => {
          const isOpen = open.includes(group.name);
          return (
            <div key={group.name} className="bg-white rounded-xl border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(group.name)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-muted" /> : <ChevronRight className="w-4 h-4 text-muted" />}
                <h2 className="text-sm font-semibold text-gray-900">{group.name}</h2>
                <span className="text-xs text-muted">({group.keys.length} parameters)</span>
              </button>
              {isOpen ? (
                <div className="overflow-x-auto border-t border-border">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-border">
                        <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted uppercase">Parameter</th>
                        <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted uppercase">Value</th>
                        <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {group.keys.map((key) => {
                        const value = getDisplayValue(key);
                        const isDirty = key in staged;
                        const isSaving = saving[key] ?? false;
                        const error = errors[key];
                        return (
                          <tr key={key} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-xs font-mono text-nipost-green">{key}</td>
                            <td className="px-4 py-1.5">
                              <input
                                type="text"
                                value={value}
                                aria-label={key}
                                onChange={(e) => stageValue(key, e.target.value)}
                                className={`w-full px-2 py-1.5 border rounded text-xs ${
                                  isDirty ? 'border-amber-300 bg-amber-50' : 'border-border'
                                }`}
                              />
                              {error ? <p className="text-[10px] text-red-600 mt-0.5">{error}</p> : null}
                            </td>
                            <td className="px-4 py-1.5">
                              <div className="flex items-center gap-2">
                                {isDirty ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => resetStaged(key)}
                                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-gray-600 border border-border rounded hover:bg-white"
                                    >
                                      <RotateCcw className="w-3 h-3" /> Revert
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isSaving}
                                      onClick={() => void save(key)}
                                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-nipost-green rounded disabled:opacity-60"
                                    >
                                      <Save className="w-3 h-3" /> {isSaving ? 'Saving…' : 'Save'}
                                    </button>
                                  </>
                                ) : (
                                  <span className="text-[10px] text-muted">No changes</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
